import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Lock, Plus, RotateCw, X } from "lucide-react";
import { useTabs } from "../../contexts/TabsContext";
import { getIconForHref, isDocsHref } from "../../config/navigation";

const SETTLE_MS = 160;

// Browser-style tab strip in the app header. Desktop only — mobile keeps the
// bottom nav. Click switches, × / middle-click closes, + opens a new tab,
// ⟳ soft-reloads only the active tab.
//
// Style: a grounded gray "track" (segmented-control look) holds the tabs so the
// strip never reads as empty. INACTIVE tabs are flat text on the track and are
// content-sized (they compress toward icon-only as the strip fills, so each
// ends up a different width). The ACTIVE tab is a solid raised pill with a
// soft glow that expands to fill the free space — the clear, high-contrast
// "current page" marker. Docs-section tabs use the success/green accent
// instead of the usual orange, so the Docs workspace reads as a distinct
// section from the rest of the ERP.
const TabBar = () => {
  const { tabs, activeId, activateTab, closeTab, toggleTabLock, newTab, reloadTab, moveTabToIndex, openInNewTab } =
    useTabs();
  const tabRefs = useRef({});
  const containerRef = useRef(null);
  // Mutable "live" drag data, read/written from the window pointermove/up
  // handlers (which must never see a stale closure); `drag` state below is a
  // shallow copy of it taken after every mutation, purely to trigger renders.
  const dragDataRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // Sidebar module dragged in from outside (native HTML5 DnD, separate from
  // the pointer-based reorder above) — highlights the strip as a drop target.
  const [moduleDropHover, setModuleDropHover] = useState(false);

  // Keep the active tab visible when switching via keyboard or overflow.
  useEffect(() => {
    tabRefs.current[activeId]?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [activeId, tabs.length]);

  const onPointerMove = useCallback((e) => {
    const info = dragDataRef.current;
    if (!info || info.settling) return;
    const dx = e.clientX - info.startX;
    if (!info.moved && Math.abs(dx) > 3) info.moved = true;
    const rawOffset = Math.round(dx / info.step);
    info.dx = dx;
    info.currentIndex = Math.max(0, Math.min(info.tabCount - 1, info.startIndex + rawOffset));
    setDrag({ ...info });
  }, []);

  const endDrag = useCallback(() => {
    const info = dragDataRef.current;
    if (!info) return;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    document.body.style.removeProperty("user-select");
    if (info.moved && info.currentIndex !== info.startIndex) {
      moveTabToIndex(info.id, info.currentIndex);
    }
    // Settle: snap the dragged tab from "under the pointer" back to its slot
    // with a short eased transition instead of popping there instantly.
    info.settling = true;
    info.dx = 0;
    setDrag({ ...info });
    window.setTimeout(() => {
      if (dragDataRef.current === info) {
        dragDataRef.current = null;
        setDrag(null);
      }
    }, SETTLE_MS);
  }, [onPointerMove, moveTabToIndex]);

  // Clean up window listeners if the strip unmounts mid-drag.
  useEffect(() => () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
  }, [onPointerMove, endDrag]);

  const beginDrag = (e, tab, index) => {
    if (e.button !== 0) return; // primary button only
    if (e.target.closest("button")) return; // let lock/close behave normally
    const el = tabRefs.current[tab.id];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gapStr = containerRef.current && getComputedStyle(containerRef.current).columnGap;
    const gap = parseFloat(gapStr) || 4;
    const info = {
      id: tab.id,
      startIndex: index,
      currentIndex: index,
      startX: e.clientX,
      step: rect.width + gap,
      dx: 0,
      moved: false,
      settling: false,
      tabCount: tabs.length,
    };
    dragDataRef.current = info;
    setDrag({ ...info });
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
  };

  return (
    <div
      data-testid="tab-bar"
      className={`flex items-center gap-1.5 w-full min-w-0 rounded-lg bg-muted/40 p-1 transition-colors ${
        moduleDropHover ? "ring-2 ring-primary/60 bg-primary/5" : "ring-1 ring-border/40"
      }`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes("application/x-erp-tab")) return;
        e.preventDefault(); // allow drop
        e.dataTransfer.dropEffect = "copy";
        if (!moduleDropHover) setModuleDropHover(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return; // still inside the strip
        setModuleDropHover(false);
      }}
      onDrop={(e) => {
        const raw = e.dataTransfer.getData("application/x-erp-tab");
        if (!raw) return;
        e.preventDefault();
        setModuleDropHover(false);
        try {
          const { href, title } = JSON.parse(raw);
          if (href) openInNewTab(href, title);
        } catch {
          // malformed payload — ignore the drop
        }
      }}
    >
      <div ref={containerRef} className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto scrollbar-hide">
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeId;
          const isDocs = isDocsHref(tab.href);
          const Icon = getIconForHref(tab.href) || FileText;

          // Sliding-neighbors animation: the dragged tab tracks the pointer
          // 1:1 (no transition); every tab between its start and current slot
          // slides over by one tab-width (with a transition) to open a gap —
          // same feel as Chrome's tab strip.
          let dragStyle;
          let dragClass = "";
          if (drag) {
            if (tab.id === drag.id) {
              dragStyle = {
                transform: `translateX(${drag.settling ? 0 : drag.dx}px)`,
                transition: drag.settling ? `transform ${SETTLE_MS}ms ease` : "none",
                zIndex: 40,
              };
              dragClass = "shadow-lg ring-2 ring-primary/40 scale-[1.02] cursor-grabbing";
            } else if (!drag.settling) {
              let shift = 0;
              if (drag.startIndex < drag.currentIndex && index > drag.startIndex && index <= drag.currentIndex) {
                shift = -drag.step;
              } else if (drag.startIndex > drag.currentIndex && index >= drag.currentIndex && index < drag.startIndex) {
                shift = drag.step;
              }
              if (shift !== 0) {
                dragStyle = { transform: `translateX(${shift}px)`, transition: `transform ${SETTLE_MS}ms ease` };
              }
            }
          }

          return (
            <div
              key={tab.id}
              ref={(el) => (tabRefs.current[tab.id] = el)}
              data-testid="tab-item"
              data-tab-id={tab.id}
              role="tab"
              aria-selected={isActive}
              title={tab.title}
              style={dragStyle}
              onPointerDown={(e) => beginDrag(e, tab, index)}
              onClick={() => {
                if (dragDataRef.current?.moved) return; // suppress the click that follows a real drag
                activateTab(tab.id);
              }}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault(); // no autoscroll on middle-click
              }}
              onAuxClick={(e) => {
                if (e.button === 1) closeTab(tab.id); // no-op on locked tabs
              }}
              className={`group relative flex items-center gap-1.5 h-8 w-40 flex-shrink-0 pl-3 pr-1.5 text-xs cursor-pointer select-none transition-colors duration-200 ease-out animate-in fade-in zoom-in-95 border ${dragClass} ${
                isActive
                  ? isDocs
                    ? "rounded-t-md rounded-b-none bg-success text-white font-semibold border-success shadow-sm shadow-success/30"
                    : "rounded-t-md rounded-b-none bg-primary text-white font-semibold border-primary shadow-sm shadow-primary/30"
                  : "rounded-md bg-card text-muted-foreground font-medium border-border hover:bg-muted/60 hover:text-foreground"
              }`}
            >
              <Icon
                className={`w-4 h-4 flex-shrink-0 transition-colors ${
                  isActive ? "text-white" : "text-muted-foreground group-hover:text-foreground"
                }`}
              />
              <span className="truncate flex-1 min-w-0">{tab.title}</span>
              {tab.locked ? (
                <button
                  type="button"
                  data-testid="tab-lock"
                  title="Locked — click to unlock, then close"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTabLock(tab.id);
                  }}
                  className={`flex-shrink-0 grid place-items-center w-4 h-4 rounded transition-all opacity-100 ${
                    isActive
                      ? "text-white/90 hover:text-white hover:bg-white/25"
                      : "text-muted-foreground hover:text-foreground hover:bg-foreground/10"
                  }`}
                >
                  <Lock className="w-3 h-3" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="tab-close"
                  title="Close tab (Ctrl+↑)"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}
                  className={`flex-shrink-0 grid place-items-center w-4 h-4 rounded transition-all ${
                    isActive
                      ? "text-white/80 hover:text-white hover:bg-white/25 opacity-100"
                      : "text-muted-foreground hover:text-foreground hover:bg-foreground/10 opacity-0 group-hover:opacity-100"
                  }`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mx-0.5 h-4 w-px bg-border/70 flex-shrink-0" />
      <button
        type="button"
        data-testid="tab-add"
        title="New tab (Ctrl+↓)"
        onClick={newTab}
        className="flex-shrink-0 grid place-items-center w-7 h-7 rounded-md border border-border bg-card text-muted-foreground hover:bg-muted/60 hover:text-primary transition-all"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        data-testid="tab-reload"
        title="Reload tab (F5 / Ctrl+R)"
        onClick={reloadTab}
        className="flex-shrink-0 grid place-items-center w-7 h-7 rounded-md border border-border bg-card text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-all"
      >
        <RotateCw className="w-3 h-3" />
      </button>
    </div>
  );
};

export default TabBar;
